import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Text = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📝 CSS Text</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS provides powerful properties to style and control text appearance on
        webpages. You can change <strong>color</strong>,{" "}
        <strong>alignment</strong>, <strong>decoration</strong>,{" "}
        <strong>transform</strong>, <strong>spacing</strong>, and more.
      </p>

      {/* Section: Text Color */}
      <div className="tutorial-section">
        <h2 className="section-title">Text Color</h2>
        <p>
          The <code>color</code> property sets the color of the text.
        </p>
        <CodeCopyBlock
          code={`p {
  color: red;
}

h1 {
  color: #1e90ff; /* HEX */
}

span {
  color: rgb(34, 197, 94); /* RGB */
}`}
        />
        <p style={{ color: "red" }}>This is red text</p>
        <h3 style={{ color: "#1e90ff" }}>This is blue text (#1e90ff)</h3>
        <span style={{ color: "rgb(34, 197, 94)" }}>
          This is green text (RGB)
        </span>
      </div>

      {/* Section: Text Alignment */}
      <div className="tutorial-section">
        <h2 className="section-title">Text Alignment</h2>
        <p>
          The <code>text-align</code> property aligns text within an element.
        </p>
        <CodeCopyBlock
          code={`p.left { text-align: left; }
p.center { text-align: center; }
p.right { text-align: right; }`}
        />
        <p style={{ textAlign: "left" }}>Left aligned text</p>
        <p style={{ textAlign: "center" }}>Center aligned text</p>
        <p style={{ textAlign: "right" }}>Right aligned text</p>
      </div>

      {/* Section: Text Decoration */}
      <div className="tutorial-section">
        <h2 className="section-title">Text Decoration</h2>
        <p>
          The <code>text-decoration</code> property adds or removes decorations
          like underline, overline, or line-through.
        </p>
        <CodeCopyBlock
          code={`a {
  text-decoration: none;
}

p.underline {
  text-decoration: underline;
}

p.line {
  text-decoration: line-through;
}

p.overline {
  text-decoration: overline;
}`}
        />
        <p style={{ textDecoration: "underline" }}>Underlined text</p>
        <p style={{ textDecoration: "line-through" }}>Line-through text</p>
        <p style={{ textDecoration: "overline" }}>Overlined text</p>
      </div>

      {/* Section: Text Transform */}
      <div className="tutorial-section">
        <h2 className="section-title">Text Transform</h2>
        <p>
          The <code>text-transform</code> property changes the capitalization of
          text.
        </p>
        <CodeCopyBlock
          code={`p.uppercase { text-transform: uppercase; }
p.lowercase { text-transform: lowercase; }
p.capitalize { text-transform: capitalize; }`}
        />
        <p style={{ textTransform: "uppercase" }}>uppercase text</p>
        <p style={{ textTransform: "lowercase" }}>LOWERCASE TEXT</p>
        <p style={{ textTransform: "capitalize" }}>this text is capitalized</p>
      </div>

      {/* Section: Text Spacing */}
      <div className="tutorial-section">
        <h2 className="section-title">Text Spacing</h2>
        <p>
          CSS allows control over letter, word, and line spacing using{" "}
          <code>letter-spacing</code>, <code>word-spacing</code>, and{" "}
          <code>line-height</code>.
        </p>
        <CodeCopyBlock
          code={`p.letter { letter-spacing: 5px; }
p.word { word-spacing: 15px; }
p.line { line-height: 2; }`}
        />
        <p style={{ letterSpacing: "5px" }}>Letter spacing 5px</p>
        <p style={{ wordSpacing: "15px" }}>Word spacing 15px between words</p>
        <p style={{ lineHeight: "2" }}>
          This paragraph has increased <br /> line height (2).
        </p>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know the basics of <strong>text styling</strong>, check
          out{" "}
          <a href="/css/fonts" className="learn-more">
            CSS Fonts
          </a>{" "}
          to learn how to style text with different font families and sizes.
        </p>
      </div>
    </div>
  );
};

export default CSS_Text;
